import React, { useEffect, useState } from "react";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
    ResponsiveContainer,
    Cell,
    ReferenceLine,
} from "recharts";
import { format, parseISO, subMonths, addMonths } from "date-fns";
import { sv } from "date-fns/locale";
import './InvoiceBarChart.scss';

interface Invoice {
    billingDate: string;
    priceExcMoms: number;
}

interface InvoiceBarChartProps {
    invoiceData: Invoice[];
    onBarClick: (date: Date) => void;
    selectedDate: Date | null;
    loading: boolean;
}

const InvoiceBarChart: React.FC<InvoiceBarChartProps> = ({ invoiceData, onBarClick, selectedDate, loading }) => {
    const [monthlyData, setMonthlyData] = useState<{ month: string; total: number; invoiceCount: number }[]>([]);

    useEffect(() => {
        const twelveMonthsAgo = subMonths(new Date(), 12);

        const allMonths = [];
        for (let i = 0; i <= 12; i++) {
            const month = format(addMonths(twelveMonthsAgo, i), "yyyy-MM");
            allMonths.push({ month, total: 0, invoiceCount: 0 });
        }

        const filteredData = invoiceData.filter(invoice => {
            const invoiceDate = parseISO(invoice.billingDate);
            return invoiceDate >= twelveMonthsAgo;
        });

        const summarizedData = filteredData.reduce((acc, invoice) => {
            const month = format(parseISO(invoice.billingDate), "yyyy-MM");
            const existingMonth = acc.find((item) => item.month === month);

            if (existingMonth) {
                existingMonth.total += invoice.priceExcMoms;
                existingMonth.invoiceCount += 1;
            } else {
                acc.push({ month, total: invoice.priceExcMoms, invoiceCount: 1 });
            }

            return acc;
        }, [] as { month: string; total: number; invoiceCount: number }[]);

        const mergedData = allMonths.map(month => {
            const existingMonth = summarizedData.find(item => item.month === month.month);
            return existingMonth || month;
        });

        setMonthlyData(mergedData);
    }, [invoiceData]);

    const CustomTooltip = ({ active, payload, label }: { active?: boolean; payload?: any[]; label?: string }) => {
        if (active && payload && payload.length) {
            const monthName = format(parseISO(label + "-01"), "MMMM yyyy", { locale: sv });
            const totalValue = payload[0].value; // This could be negative

            return (
                <div className="invoice-barchart-tooltip">
                    <p className="invoice-barchart-tooltip-header">
                        {monthName.charAt(0).toUpperCase() + monthName.slice(1)}
                    </p>
                    <p className="invoice-barchart-tooltip-text">Summa exkl. moms: {totalValue.toLocaleString()} kr</p>
                    <p className="invoice-barchart-tooltip-text">Antal fakturor: {payload[0].payload.invoiceCount}</p>
                </div>
            );
        }
        return null;
    };

    const renderLegend = () => {
        return (
            <div className="invoice-barchart-legend">
                <div className="invoice-barchart-legend-icon"></div>
                Summa exkl. moms
            </div>
        );
    };

    const yAxisTickFormatter = (value: number) => {
        return value >= 1000000 || value <= -1000000
            ? `${(value / 1000000).toFixed(1)}M`
            : value.toLocaleString();
    };

    return (
        <div className="invoice-barchart" style={{ pointerEvents: loading ? 'none' : 'auto', opacity: loading ? 0.5 : 1 }}>
            <div className="invoice-barchart-graph-wrapper">
                <ResponsiveContainer>
                    <BarChart
                        data={monthlyData}
                        margin={{ right: 20, left: 20}} // Increase the left margin
                    >

                        <XAxis dataKey="month" />
                        <YAxis tickFormatter={yAxisTickFormatter} />
                        <Tooltip content={<CustomTooltip />} cursor={false} />
                        <Legend content={renderLegend} />

                        {/* Add ReferenceLine at y=0 */}
                        <ReferenceLine y={0} stroke="#000" strokeWidth={0.4} />

                        <Bar dataKey="total" fill="#173d54">
                            {monthlyData.map((entry, index) => {
                                const isSelected = selectedDate && format(selectedDate, "yyyy-MM") === entry.month;
                                return (
                                    <Cell
                                        key={`cell-${index}`}
                                        fill={isSelected ? "#173d54" : "#8fa9ba"}
                                        onClick={loading ? undefined : () => onBarClick(new Date(entry.month + "-01"))}
                                    />
                                );
                            })}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

export default InvoiceBarChart;
